import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const ImageCarousel = ({ 
  images = [], 
  mainImage, 
  alt = 'Image',
  className = '',
  aspectRatio = 'video', // 'video' (16:9), 'square', 'portrait'
  showDots = true,
  showArrows = true,
  autoPlay = false,
  autoPlayInterval = 5000
}) => {
  // Combine main image with gallery images, filter out empty strings
  const allImages = [mainImage, ...images].filter(img => img && img.trim() !== '');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Only show carousel controls if there are multiple images
  const hasMultipleImages = allImages.length > 1;

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  }, [allImages.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && hasMultipleImages && !isHovered) {
      const interval = setInterval(goToNext, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [autoPlay, hasMultipleImages, isHovered, goToNext, autoPlayInterval]);

  // Aspect ratio classes
  const aspectClasses = {
    video: 'aspect-video',
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
  };

  if (allImages.length === 0) {
    return (
      <div className={cn('bg-gray-200 flex items-center justify-center', aspectClasses[aspectRatio], className)}>
        <span className="text-gray-400">No image</span>
      </div>
    );
  }

  return (
    <div 
      className={cn('relative overflow-hidden group', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Images */}
      <div className={cn('relative w-full', aspectClasses[aspectRatio])}>
        {allImages.map((image, index) => (
          <img
            key={index}
            src={image}
            alt={`${alt} ${index + 1}`}
            className={cn(
              'absolute inset-0 w-full h-full object-cover transition-opacity duration-500',
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            )}
          />
        ))}
      </div>

      {/* Navigation Arrows */}
      {hasMultipleImages && showArrows && (
        <>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToPrev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToNext(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
            aria-label="Next image"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {hasMultipleImages && showDots && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
          {allImages.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goToSlide(index); }}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-300',
                index === currentIndex 
                  ? 'bg-white w-4' 
                  : 'bg-white/50 hover:bg-white/75'
              )}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {hasMultipleImages && (
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full z-10">
          {currentIndex + 1} / {allImages.length}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;
